import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { permissionService } from '../services/permission-service';
import { alMarcar, conImplicadas } from '../estructura-de-permisos';
import type { PermissionsConfig } from '../types/permission.types';

interface PermissionState {
  config: PermissionsConfig | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Edited state per role (dirty tracking)
  editedPermissions: Record<string, string[]>;
  editedMenuItems: Record<string, string[]>;

  // Actions
  fetchConfig: () => Promise<void>;
  setRolePermissions: (role: string, permissions: string[]) => void;
  setRoleMenuItems: (role: string, menuItems: string[]) => void;
  togglePermission: (role: string, permission: string) => void;
  toggleMenuItem: (role: string, menuItem: string) => void;
  saveRole: (role: string) => Promise<void>;
  resetRole: (role: string) => Promise<void>;
  isDirty: (role: string) => boolean;
}

export const usePermissionStore = create<PermissionState>()(
  devtools(
    (set, get) => ({
      config: null,
      isLoading: false,
      isSaving: false,
      error: null,
      editedPermissions: {},
      editedMenuItems: {},

      fetchConfig: async () => {
        try {
          set({ isLoading: true, error: null });
          const config = await permissionService.getAll();

          // Initialize edited state from server data
          //
          // `conImplicadas` reconcilia lo que trae el servidor con «ver
          // implícito» (Reglas: cualquiera de escritura implica ver). Los
          // roles base ya llegan resueltos (`getEffectivePermissions`), así
          // que acá es un resguardo sin costo — devuelve la MISMA lista si no
          // falta nada; la misma función que usa el diálogo de roles
          // personalizados (Tarea 3b), donde sí hace falta.
          const editedPermissions: Record<string, string[]> = {};
          const editedMenuItems: Record<string, string[]> = {};
          for (const role of config.roles) {
            editedPermissions[role.role] = conImplicadas([...role.permissions]);
            editedMenuItems[role.role] = [...role.menuItems];
          }

          set({ config, editedPermissions, editedMenuItems, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al cargar permisos',
            isLoading: false,
          });
        }
      },

      setRolePermissions: (role, permissions) => {
        set((state) => ({
          editedPermissions: { ...state.editedPermissions, [role]: permissions },
        }));
      },

      setRoleMenuItems: (role, menuItems) => {
        set((state) => ({
          editedMenuItems: { ...state.editedMenuItems, [role]: menuItems },
        }));
      },

      togglePermission: (role, permission) => {
        /*
          Fix round 1 (post Tarea 4): la pestaña «Roles Base» dibuja su propia
          tabla (no pasa por `DialogoDePermisos`), pero el toggle vive ACÁ, en
          el store — así que es el único lugar donde hace falta enchufar
          `alMarcar` para que las dos pantallas obedezcan la misma regla
          (Reglas: cualquiera de escritura implica ver). Antes sumaba o sacaba
          la llave sola, sin mirar `implica`.
        */
        set((state) => {
          const current = state.editedPermissions[role] || [];
          const marcado = !current.includes(permission);
          return {
            editedPermissions: {
              ...state.editedPermissions,
              [role]: alMarcar(current, permission, marcado),
            },
          };
        });
      },

      toggleMenuItem: (role, menuItem) => {
        set((state) => {
          const current = state.editedMenuItems[role] || [];
          const next = current.includes(menuItem)
            ? current.filter((m) => m !== menuItem)
            : [...current, menuItem];
          return {
            editedMenuItems: { ...state.editedMenuItems, [role]: next },
          };
        });
      },

      saveRole: async (role) => {
        try {
          set({ isSaving: true, error: null });
          const { editedPermissions, editedMenuItems } = get();
          const permissions = editedPermissions[role] || [];
          const menuItems = editedMenuItems[role] || [];

          const updated = await permissionService.updateRole(role, permissions, menuItems);

          // Update config state
          set((state) => {
            if (!state.config) return {};
            const roles = state.config.roles.map((r) => (r.role === role ? updated : r));
            return {
              config: { ...state.config, roles },
              isSaving: false,
            };
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al guardar permisos',
            isSaving: false,
          });
          throw error;
        }
      },

      resetRole: async (role) => {
        try {
          set({ isSaving: true, error: null });
          const updated = await permissionService.resetRole(role);

          set((state) => {
            if (!state.config) return {};
            const roles = state.config.roles.map((r) => (r.role === role ? updated : r));
            return {
              config: { ...state.config, roles },
              editedPermissions: {
                ...state.editedPermissions,
                [role]: conImplicadas([...updated.permissions]),
              },
              editedMenuItems: {
                ...state.editedMenuItems,
                [role]: [...updated.menuItems],
              },
              isSaving: false,
            };
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al restablecer permisos',
            isSaving: false,
          });
          throw error;
        }
      },

      isDirty: (role) => {
        const { config, editedPermissions, editedMenuItems } = get();
        if (!config) return false;
        const original = config.roles.find((r) => r.role === role);
        if (!original) return false;

        const currentPerms = editedPermissions[role] || [];
        const currentMenu = editedMenuItems[role] || [];

        const permsMatch =
          currentPerms.length === original.permissions.length &&
          currentPerms.every((p) => original.permissions.includes(p));

        const menuMatch =
          currentMenu.length === original.menuItems.length &&
          currentMenu.every((m) => original.menuItems.includes(m));

        return !permsMatch || !menuMatch;
      },
    }),
    { name: 'PermissionStore' }
  )
);
